/**
 * EAC export aggregation — the single source of truth for collapsing
 * projectionRows into the (jobId, costType, phaseCode) lines that get written
 * to the FusionEAC CSV (JDE upload) and the debug Excel export.
 *
 * Kept as a pure helper so both export paths in projectionSync.js share one
 * implementation and the row-set/value invariants can be regression-tested.
 *
 * Invariants (asserted in __tests__/eacAggregation.test.js):
 *   - blank / '' / 'undefined' phaseCode collapses to the base phase 100105
 *     (JDE rejects blank fields; matches addToTotals' own blank handling), and so
 *     does a phase that isn't digits (a stray 't' typed in an ETC row, 10/30)
 *   - duplicate keys are summed, never double-counted or fanned out
 *   - the output key set equals the normalized input key set (no invented /
 *     dropped rows)
 *   - the first non-empty tier for a key is kept
 */

const BASE_PHASE = '100105';

function normalizePhase(phaseCode) {
    if (!phaseCode || phaseCode === '' || phaseCode === 'undefined') return BASE_PHASE;
    if (!/^\d+$/.test(String(phaseCode).trim())) return BASE_PHASE;
    return phaseCode;
}

function parseValue(value) {
    const raw =
        value !== undefined && value !== null && value !== ''
            ? String(value).replace(/,/g, '')
            : '0';
    const num = parseFloat(raw);
    return isNaN(num) ? 0 : num;
}

function aggregateEACRows(projectionRows) {
    const aggMap = new Map();
    for (const row of projectionRows || []) {
        const jobId = row.jobId || '';
        const costType = row.costType || '';
        const phaseCode = normalizePhase(row.phaseCode);
        const key = `${jobId}|${costType}|${phaseCode}`;
        const add = parseValue(row.value);
        const existing = aggMap.get(key);
        if (existing) {
            existing.value += add;
            if (!existing.tier && row.tier) existing.tier = row.tier;
        } else {
            aggMap.set(key, { jobId, costType, phaseCode, value: add, tier: row.tier || '' });
        }
    }
    return [...aggMap.values()];
}

/**
 * 0 lines for HA accounts the upload doesn't carry. The JDE EAC upload only sets
 * the accounts in the file, so an account with an HA balance but no line (an old
 * summary 799999 subsidiary, a phase Fusion dropped) keeps its balance and the job
 * double-counts. A 0 line for each one clears it.
 *
 * aggregatedRows: aggregateEACRows output for the upload.
 * haDetail: { BU_ID, Object_Account, Subsidiary, Total_Amount } per HA account.
 * Only jobs that are in the upload are touched. Accounts with a blank subsidiary
 * can't be addressed by a jobId.costType.phaseCode line and are skipped.
 */
function haZeroLines(aggregatedRows, haDetail, tierByJob = {}) {
    const inFile = new Set();
    const jobs = new Set();
    for (const r of aggregatedRows || []) {
        inFile.add(`${r.jobId}|${r.costType}|${r.phaseCode}`);
        jobs.add(r.jobId);
    }
    const zeroLines = [];
    const seen = new Set();
    for (const h of haDetail || []) {
        const jobId = String(h.BU_ID ?? '').trim();
        const costType = String(h.Object_Account ?? '').trim();
        const phaseCode = String(h.Subsidiary ?? '').trim();
        const key = `${jobId}|${costType}|${phaseCode}`;
        const balance = Number(h.Total_Amount) || 0;
        if (!jobs.has(jobId) || !phaseCode || inFile.has(key) || seen.has(key)) continue;
        if (Math.abs(balance) < 0.005) continue;
        seen.add(key);
        zeroLines.push({ jobId, costType, phaseCode, value: 0, tier: tierByJob[jobId] || '', clears: balance });
    }
    return zeroLines;
}

/**
 * Fusion puts forecast lines with no phase on a no-phase account: 100105, or
 * 100102 for 52400 and 100101 for 54200 (addToTotals). Where the job has no such
 * account in JDE, the upload books the amount onto some other account (sometimes
 * in another cost group, 10/30 even revenue), so the line moves to the first
 * no-phase account the job does have for that cost type, in NO_PHASE order.
 * Lines whose account exists stay put; if none exists the line is left as is.
 *
 * aggregatedRows: aggregateEACRows output. accounts: Set of 'job|object|subsidiary'
 * that exist in JDE (F0901). Returns { rows, moved } with rows re-aggregated so a
 * moved line merges into an existing line on its new account.
 */
const FALLBACK_PHASE = '100101';
const NO_PHASE = [FALLBACK_PHASE, BASE_PHASE, '100102'];

function remapMissingBasePhase(aggregatedRows, accounts) {
    const merged = new Map();
    let moved = 0;
    for (const r of aggregatedRows || []) {
        let phaseCode = r.phaseCode;
        if (NO_PHASE.includes(phaseCode) && !accounts.has(`${r.jobId}|${r.costType}|${phaseCode}`)) {
            const target = NO_PHASE.find(p => accounts.has(`${r.jobId}|${r.costType}|${p}`));
            if (target) {
                phaseCode = target;
                moved++;
            }
        }
        const key = `${r.jobId}|${r.costType}|${phaseCode}`;
        const existing = merged.get(key);
        if (existing) {
            existing.value += r.value;
            if (!existing.tier && r.tier) existing.tier = r.tier;
        } else {
            merged.set(key, { ...r, phaseCode });
        }
    }
    return { rows: [...merged.values()], moved };
}

// ES exports, not module.exports: the production build compiles syntax like
// object spread to an injected `import` of a Babel helper, which makes webpack
// treat this file as an ES module and ignore module.exports — every import of
// it then fails to compile. The root jest suite still require()s it through
// babel.config.js.
export { aggregateEACRows, haZeroLines, remapMissingBasePhase, normalizePhase, parseValue, BASE_PHASE, FALLBACK_PHASE };
